import { BufferAttribute, BufferGeometry, Color, Mesh, Vector4, type Object3D, type ShaderMaterial } from "three";

/** Vagas por lote (o tamanho dos arrays de uniform `uSlot`/`uSlotColor` dos shaders). */
export const DECAL_SLOTS = 64;

/** Os atributos que cada peça traz (em coordenadas do mundo, colada ao chão). */
const ATTRIBUTES = [["position", 3], ["aRadial", 1], ["aAngular", 1]] as const;

interface Piece {
  geometry: BufferGeometry;
  chunk: Chunk;
  slot: number;
}

interface Chunk {
  mesh: Mesh<BufferGeometry, ShaderMaterial>;
  pieces: Set<Piece>;
  free: number[];
  dirty: boolean;
}

/**
 * Decalques no chão num draw só (revisão da Fase 13: os avisos de ataque e os perigos eram um draw e um
 * material cada, e numa luta longa passavam do teto de 250 draw calls). Cada peça é uma geometria já no
 * mundo (com `aRadial`/`aAngular`), juntada às outras do lote com o atributo `aSlot`; o que muda por
 * peça (andamento, tipo, cor) vai em `uSlot[slot]` (vec4) e `uSlotColor[slot]` (vec3), lidos no
 * vértice. Lotes de `DECAL_SLOTS` peças; a malha só fica na cena enquanto o lote tem peça. A geometria
 * junta é refeita quando uma peça entra ou sai (raro: avisos e perigos nascem por evento).
 */
export class DecalBatch {
  private readonly root: Object3D;
  private readonly material: () => ShaderMaterial;
  private readonly renderOrder: number;
  private readonly chunks: Chunk[] = [];
  private readonly pieces = new Set<Piece>();

  constructor(root: Object3D, material: () => ShaderMaterial, renderOrder: number) {
    this.root = root;
    this.material = material;
    this.renderOrder = renderOrder;
  }

  /** Entra uma peça (a geometria passa a ser do lote, que a descarta ao sair). */
  add(geometry: BufferGeometry): Piece {
    let chunk = this.chunks.find((c) => c.free.length > 0);
    if (!chunk) {
      const material = this.material();
      material.uniforms["uSlot"] = { value: Array.from({ length: DECAL_SLOTS }, () => new Vector4()) };
      material.uniforms["uSlotColor"] = { value: Array.from({ length: DECAL_SLOTS }, () => new Color()) };
      const mesh = new Mesh(new BufferGeometry(), material);
      mesh.renderOrder = this.renderOrder;
      // As peças ficam espalhadas pelo mundo: o lote não é cortado pela caixa (a geometria muda sempre).
      mesh.frustumCulled = false;
      chunk = { mesh, pieces: new Set(), free: Array.from({ length: DECAL_SLOTS }, (_, i) => DECAL_SLOTS - 1 - i), dirty: false };
      this.chunks.push(chunk);
    }
    const piece: Piece = { geometry, chunk, slot: chunk.free.pop()! };
    chunk.pieces.add(piece);
    chunk.dirty = true;
    this.pieces.add(piece);
    return piece;
  }

  /** O que muda por peça: `slot` (vec4) e a cor (linear). */
  set(piece: Piece, x: number, y: number, z: number, w: number, color?: Color): void {
    const u = piece.chunk.mesh.material.uniforms;
    (u["uSlot"]!.value as Vector4[])[piece.slot]!.set(x, y, z, w);
    if (color) (u["uSlotColor"]!.value as Color[])[piece.slot]!.copy(color);
  }

  remove(piece: Piece): void {
    if (!this.pieces.delete(piece)) return;
    piece.chunk.pieces.delete(piece);
    piece.chunk.free.push(piece.slot);
    piece.chunk.dirty = true;
    piece.geometry.dispose();
  }

  /** Um uniform igual para todo o lote (as opções: sem lampejo, modo daltônico). */
  uniform(name: string, value: number): void {
    for (const c of this.chunks) c.mesh.material.uniforms[name]!.value = value;
  }

  /** Refaz as geometrias que mudaram e põe ou tira as malhas da cena. */
  flush(): void {
    for (const chunk of this.chunks) {
      if (!chunk.dirty) continue;
      chunk.dirty = false;
      chunk.mesh.geometry.dispose();
      chunk.mesh.geometry = merge([...chunk.pieces]);
      const shown = chunk.mesh.parent !== null;
      if (chunk.pieces.size > 0 && !shown) this.root.add(chunk.mesh);
      if (chunk.pieces.size === 0 && shown) this.root.remove(chunk.mesh);
    }
  }

  get count(): number {
    return this.pieces.size;
  }

  /** Draw calls do lote agora (para a contagem dos testes). */
  get draws(): number {
    return this.chunks.filter((c) => c.pieces.size > 0).length;
  }

  dispose(): void {
    for (const piece of [...this.pieces]) this.remove(piece);
    for (const chunk of this.chunks) {
      this.root.remove(chunk.mesh);
      chunk.mesh.geometry.dispose();
      chunk.mesh.material.dispose();
    }
    this.chunks.length = 0;
  }
}

/** As peças numa geometria só, com `aSlot` por vértice (índices deslocados). */
function merge(pieces: readonly Piece[]): BufferGeometry {
  let vertices = 0;
  let indices = 0;
  for (const p of pieces) {
    vertices += p.geometry.getAttribute("position").count;
    indices += p.geometry.index?.count ?? p.geometry.getAttribute("position").count;
  }
  const out = new BufferGeometry();
  const arrays = ATTRIBUTES.map(([, size]) => new Float32Array(vertices * size));
  const slot = new Float32Array(vertices);
  const index = vertices > 65535 ? new Uint32Array(indices) : new Uint16Array(indices);
  let v = 0;
  let i = 0;
  for (const p of pieces) {
    const count = p.geometry.getAttribute("position").count;
    ATTRIBUTES.forEach(([name, size], k) => arrays[k]!.set(p.geometry.getAttribute(name).array as Float32Array, v * size));
    slot.fill(p.slot, v, v + count);
    const src = p.geometry.index;
    if (src) for (let j = 0; j < src.count; j++) index[i + j] = src.getX(j) + v;
    else for (let j = 0; j < count; j++) index[i + j] = v + j;
    i += src ? src.count : count;
    v += count;
  }
  ATTRIBUTES.forEach(([name, size], k) => out.setAttribute(name, new BufferAttribute(arrays[k]!, size)));
  out.setAttribute("aSlot", new BufferAttribute(slot, 1));
  out.setIndex(new BufferAttribute(index, 1));
  return out;
}
